import React, { type CSSProperties } from 'react';
import { Badge, Card, EmptyState, Text } from '@shopify/polaris';
import { Queries } from '../query/query-client';
import { useQuery } from '@tanstack/react-query';
import type { WidgetConfig } from '../store/widget.store';
import { pagePlacements, pagesToSelect } from '../constants/lists';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCartFlatbed,
    faAlarmClock,
    faCalendarDays,
    faClock,
    faEye,
    faTruck,
    faFont,
} from '@fortawesome/free-solid-svg-icons';
import { WidgetType } from '../widgets/widgets';

function formatDeadlineDate(date: string): React.ReactNode {
    const prefix = <Text as="span">Countdown to </Text>;
    if (!dayjs(date).isValid()) {
        return (
            <>
                {prefix} <Badge tone="critical">Invalid date</Badge>
            </>
        );
    }

    if (dayjs(date).isBefore(dayjs())) {
        return (
            <>
                {prefix} <Text as="span">{dayjs(date).format('MMMM D, YYYY h:mm A')}</Text>{' '}
                <Badge tone="critical">Expired</Badge>
            </>
        );
    }

    return (
        <>
            {prefix} <Text as="span">{dayjs(date).format('MMMM D, YYYY h:mm A')}</Text>
        </>
    );
}

function getWidgetIcon(timerType: WidgetConfig['timerType'], widgetType: WidgetConfig['widgetType']): React.ReactNode {
    switch (widgetType) {
        case WidgetType.COUNTDOWN:
            if (timerType === 'fixed_date') {
                return <FontAwesomeIcon icon={faCalendarDays} size="2x" />;
            }
            return <FontAwesomeIcon icon={faAlarmClock} size="2x" />;
        case WidgetType.INVENTORY:
            return <FontAwesomeIcon icon={faCartFlatbed} size="2x" />;
        case WidgetType.FREE_SHIPPING:
            return <FontAwesomeIcon icon={faTruck} size="2x" />;
        default:
            return <FontAwesomeIcon icon={faFont} size="2x" />;
    }
}

function getWidgetDescription({
    timerType,
    widgetType,
    fixedDate,
    fixedMinutes,
}: {
    timerType: WidgetConfig['timerType'];
    widgetType: WidgetConfig['widgetType'];
    fixedDate?: string;
    fixedMinutes?: number;
}): React.ReactNode {
    switch (widgetType) {
        case WidgetType.COUNTDOWN:
            if (timerType === 'fixed_date') {
                return formatDeadlineDate(fixedDate as string);
            }
            return `A ${fixedMinutes || 0}-minutes countdown`;
        case WidgetType.INVENTORY:
            return 'Inventory countdown widget';
        case WidgetType.FREE_SHIPPING:
            return 'Free shipping reminder';
        default:
            return 'Static text widget';
    }
}

function getWidgetCardVariant(widgetType: WidgetConfig['widgetType']) {
    switch (widgetType) {
        case WidgetType.COUNTDOWN:
            return 'countdown';
        case WidgetType.INVENTORY:
            return 'inventory';
        case WidgetType.FREE_SHIPPING:
            return 'free-shipping';
        default:
            return 'default';
    }
}

function getWidgetTypeLabel(widgetType: WidgetConfig['widgetType']) {
    switch (widgetType) {
        case WidgetType.COUNTDOWN:
            return 'Countdown';
        case WidgetType.INVENTORY:
            return 'Inventory';
        case WidgetType.FREE_SHIPPING:
            return 'Free shipping';
        default:
            return 'Default';
    }
}

function formatPagePlacementTag({
    page,
    position,
}: {
    page: WidgetConfig['placements'][number]['page'];
    position: WidgetConfig['placements'][number]['position'];
}) {
    const pageLabel = pagesToSelect.find(p => p.value === page)?.label || page;
    const positionLabel = pagePlacements.find(p => p.value === position)?.label || position;

    return `${pageLabel} - ${positionLabel}`;
}

export function WidgetsList() {
    const navigate = useNavigate();

    const { data: widgetsList, isLoading: isFetchingWidgets } = useQuery<WidgetConfig[]>({
        queryKey: [Queries.WIDGET_GET_ALL],
    });

    const widgets = (widgetsList ?? []) as WidgetConfig[];

    if (isFetchingWidgets && widgets.length === 0) {
        return (
            <div className="mv-widget-empty-state">
                <Text as="p" variant="bodyMd">
                    Loading widgets...
                </Text>
            </div>
        );
    }

    if (widgets.length === 0) {
        return (
            <Card>
                <EmptyState
                    heading="Create your first widget"
                    action={{ content: 'Create widget', onAction: () => navigate('/manage-widget') }}
                    image="https://cdn.shopify.com/s/files/1/2376/3301/products/emptystate-files.png"
                >
                    <p>There are no widgets yet. Start by creating one and choose where it appears in your store.</p>
                </EmptyState>
            </Card>
        );
    }

    return (
        <div className="mv-widget-grid">
            {widgets.map((item, index) => {
                const {
                    _id,
                    name,
                    active,
                    placements,
                    views,
                    timerType,
                    fixedDate,
                    fixedMinutes,
                    widgetType,
                    createdAt,
                } = item;

                return (
                    <button
                        key={_id || name}
                        type="button"
                        className={`mv-widget-card mv-widget-card--${getWidgetCardVariant(widgetType)} mv-reveal`}
                        style={{ animationDelay: `${70 + index * 40}ms` } as CSSProperties}
                        onClick={() => navigate(_id ? `/manage-widget?id=${_id}` : '/manage-widget')}
                        aria-label={`Edit ${name}`}
                    >
                        <span className="mv-widget-type-badge">{getWidgetTypeLabel(widgetType)}</span>
                        <div className="mv-widget-card-head">
                            <div className="mv-widget-icon-wrap">{getWidgetIcon(timerType, widgetType)}</div>
                            <div>
                                <h3 className="mv-widget-title">{name}</h3>
                                <div className="mv-widget-status-row">
                                    <span
                                        className={`mv-widget-status-dot ${active ? 'is-active' : 'is-inactive'}`}
                                        aria-hidden="true"
                                    />
                                    <Badge tone={active ? 'success' : 'critical'}>
                                        {active ? 'Active' : 'Inactive'}
                                    </Badge>
                                </div>
                            </div>
                        </div>

                        <div className="mv-widget-description">
                            {getWidgetDescription({
                                timerType,
                                widgetType,
                                fixedDate,
                                fixedMinutes,
                            })}
                        </div>

                        <div className="mv-widget-pill-list">
                            {placements.map(placement => (
                                <span key={`${placement.page}-${placement.position}`} className="mv-widget-pill">
                                    {formatPagePlacementTag(placement)}
                                </span>
                            ))}
                        </div>

                        <div className="mv-widget-meta">
                            <div>
                                <span className="mv-widget-meta-label">Views</span>
                                <span className="mv-widget-meta-value">
                                    <FontAwesomeIcon icon={faEye} /> {(views || 0).toLocaleString()}
                                </span>
                            </div>
                            <div>
                                <span className="mv-widget-meta-label">Created</span>
                                <span className="mv-widget-meta-value">
                                    <FontAwesomeIcon icon={faClock} />{' '}
                                    {createdAt ? dayjs(createdAt).format('MMM D, YYYY') : 'Not set'}
                                </span>
                            </div>
                        </div>
                    </button>
                );
            })}
        </div>
    );
}
